import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import { updateTagById } from 'api/tags';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Tag } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface ContactTagEditFormValues {
    tagLabel: string;
}

interface ContactTagEditFormProps {
    tag: Tag;
}

const ContactTagEditForm = ({ tag }: ContactTagEditFormProps) => {
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();
    const { error, success } = useToast();
    const methods = Form.useGivebutterForm({ defaultValues: { tagLabel: tag.name } });
    const { control, handleSubmit, path, setError, reset, resetFormButton } = methods;

    const { mutate: submit, isPending: isLoading } = useMutation<
        unknown,
        GivebutterAxiosError,
        ContactTagEditFormValues
    >({
        mutationFn: values => updateTagById(accountId, { ...tag, name: values.tagLabel }),
        onSuccess: (_response, values) => {
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.tags(accountId) });
            success('Tag updated!');
            reset({ tagLabel: values.tagLabel });
        },
        onError: e => {
            const errors = e.response?.data.errors;

            if (errors) {
                for (let i in errors) {
                    setError('tagLabel', { message: errors[i][0] });
                }
            } else {
                error('Unable to update tag');
            }

            resetFormButton();
        },
    });

    return (
        <Form.Provider {...methods}>
            <form
                onSubmit={handleSubmit(values => submit(values))}
                className="flex flex-col gap-2 p-2"
            >
                <Form.Container>
                    <Form.Label name={path('tagLabel')}>Edit tag name</Form.Label>
                    <Form.TextInputRoot control={control} name="tagLabel">
                        <Form.TextInputForm autoComplete="off" />
                    </Form.TextInputRoot>
                    <Form.Error name={path('tagLabel')} />
                </Form.Container>
                <Separator className="-mx-2 !w-auto" />
                <Form.Container>
                    <Form.SubmitButton
                        variant="none"
                        color="primary"
                        className="justify-start font-medium"
                        disabled={isLoading}
                        isSubmitting={isLoading}
                    >
                        <span>{isLoading ? 'Applying' : 'Apply'}</span>
                    </Form.SubmitButton>
                </Form.Container>
            </form>
        </Form.Provider>
    );
};

export { ContactTagEditForm };
